import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LogOut, Settings, HelpCircle, CreditCard, ChevronRight, Shield, Lock, Tag } from "lucide-react";
import MobileAppLayout from "@/components/app/MobileAppLayout";
import { useAuth } from "@/hooks/useAuth";
import { useAppMode } from "@/hooks/useAppMode";
import { useUserMemberships } from "@/hooks/useUserMemberships";

const AppProfile = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { provider } = useAppMode();
  const { data: memberships = [] } = useUserMemberships(provider?.id);

  const firstName = user?.user_metadata?.first_name || "";
  const lastName = user?.user_metadata?.last_name || "";
  const fullName = `${firstName} ${lastName}`.trim() || user?.email;

  const handleSignOut = async () => {
    await signOut();
    navigate("/app");
  };

  return (
    <MobileAppLayout title="Profile">
      <div className="px-4 py-5 space-y-5">
        {/* User info */}
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center text-xl font-semibold">
                {fullName?.charAt(0).toUpperCase() || "U"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{fullName}</p>
                <p className="text-sm text-muted-foreground truncate">
                  {user?.email}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Memberships */}
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2 px-1">
            Memberships
          </h2>
          {memberships.length === 0 ? (
            <Card>
              <CardContent className="p-4 text-sm text-muted-foreground">
                No active memberships.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {memberships.map((m) => (
                <Card key={m.id}>
                  <CardContent className="p-4 flex items-center gap-3">
                    <CreditCard className="h-5 w-5 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">
                        {m.membership.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {m.membership.membership_type === "pack_of_sessions"
                          ? `${m.sessions_remaining ?? 0} sessions left`
                          : "Active"}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Menu */}
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2 px-1">
            Account
          </h2>
          <Card>
            <CardContent className="p-0">
              <button
                onClick={() => navigate("/app/account")}
                className="w-full flex items-center gap-3 p-4 hover:bg-muted/50 transition-colors text-left border-b border-border"
              >
                <Settings className="h-5 w-5 text-muted-foreground" />
                <span className="flex-1 font-medium">Account settings</span>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </button>
              <button
                onClick={() => navigate("/app/emergency-contact")}
                className="w-full flex items-center gap-3 p-4 hover:bg-muted/50 transition-colors text-left border-b border-border"
              >
                <Shield className="h-5 w-5 text-muted-foreground" />
                <span className="flex-1 font-medium">Emergency contact</span>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </button>
              <button
                onClick={() => navigate("/app/memberships")}
                className="w-full flex items-center gap-3 p-4 hover:bg-muted/50 transition-colors text-left border-b border-border"
              >
                <CreditCard className="h-5 w-5 text-muted-foreground" />
                <span className="flex-1 font-medium">Memberships</span>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </button>
              <button
                onClick={() => navigate("/app/coupons")}
                className="w-full flex items-center gap-3 p-4 hover:bg-muted/50 transition-colors text-left border-b border-border"
              >
                <Tag className="h-5 w-5 text-muted-foreground" />
                <span className="flex-1 font-medium">Coupons</span>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </button>
              <button
                onClick={() => navigate("/app/change-password")}
                className="w-full flex items-center gap-3 p-4 hover:bg-muted/50 transition-colors text-left border-b border-border"
              >
                <Lock className="h-5 w-5 text-muted-foreground" />
                <span className="flex-1 font-medium">Change password</span>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </button>
              <button
                onClick={() => navigate("/app/support")}
                className="w-full flex items-center gap-3 p-4 hover:bg-muted/50 transition-colors text-left"
              >
                <HelpCircle className="h-5 w-5 text-muted-foreground" />
                <span className="flex-1 font-medium">Help & support</span>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </button>
            </CardContent>
          </Card>
        </div>

        <Button
          variant="outline"
          className="w-full"
          onClick={handleSignOut}
        >
          <LogOut className="h-4 w-4 mr-2" />
          Sign out
        </Button>
      </div>
    </MobileAppLayout>
  );
};

export default AppProfile;
